import React, { useState, useEffect } from 'react';
import { getQuotes } from '../../api/quotes';
import PrintButton from '../common/PrintButton';
import { quoteTotals, money, quoteDate } from '../../utils/quoteTotals';
import { QUOTES_CHANGED } from './QuoteSidebar';
import './Quotes.css';

// All quotes of a project with their totals (incl. markups), and a grand total
function QuoteSummary({ project, onOpenTab }) {
  const [quotes, setQuotes] = useState(null);

  useEffect(() => {
    if (!project?._id) return;
    const load = () => getQuotes(project._id).then(r => setQuotes(r.data)).catch(() => setQuotes([]));
    load();
    const onChange = (e) => { if (!e.detail?.project || e.detail.project === project._id) load(); };
    window.addEventListener(QUOTES_CHANGED, onChange);
    return () => window.removeEventListener(QUOTES_CHANGED, onChange);
  }, [project?._id]);

  if (!quotes) return <div className="module-wrap"><div className="empty-state">Loading…</div></div>;

  const rows = quotes.map(q => ({ q, t: quoteTotals(q) }));
  const grand = rows.reduce((s, { t }) => {
    for (const k of ['cables', 'items', 'hours', 'discount', 'net', 'vat', 'total']) s[k] += t[k];
    return s;
  }, { cables: 0, items: 0, hours: 0, discount: 0, net: 0, vat: 0, total: 0 });

  const open = (q) => onOpenTab(`quote-${q._id}`, q.name, { type: 'quote', quoteId: q._id, project });

  const printRows = [
    ...rows.map(({ q, t }) => ({ number: q.number, name: q.name, date: quoteDate(q.date), ...t })),
    { number: '', name: 'Total', date: '', ...grand },
  ];

  return (
    <div className="module-wrap">
      <div className="module-toolbar">
        <span className="module-toolbar-title">Quotes — Summary</span>
        <span className="q-muted">{quotes.length} quote{quotes.length === 1 ? '' : 's'}</span>
        {quotes.length > 0 && (
          <PrintButton
            title={`Quote summary — ${project?.name ?? ''}`}
            storageKey="quote-summary"
            rows={printRows}
            onOpenTab={onOpenTab}
            columns={[
              { key: 'number', label: 'Quote no.',       render: r => r.number },
              { key: 'name',   label: 'Name',            render: r => r.name },
              { key: 'date',   label: 'Date',            render: r => r.date },
              { key: 'cables', label: 'Cables',          render: r => money(r.cables) },
              { key: 'items',  label: 'Items',           render: r => money(r.items) },
              { key: 'hours',  label: 'Hours',           render: r => money(r.hours) },
              { key: 'disc',   label: 'Discount',        render: r => (r.discount ? `-${money(r.discount)}` : '') },
              { key: 'net',    label: 'Total excl. VAT', render: r => money(r.net) },
              { key: 'total',  label: 'Total incl. VAT', render: r => money(r.total) },
            ]}
          />
        )}
      </div>
      <div className="module-body q-body">
        {quotes.length === 0 ? (
          <div className="empty-state">No quotes in this project yet. Create one with New Quote in the side panel.</div>
        ) : (
          <div className="q-table-wrap">
            <table className="data-table q-table q-summary">
              <thead>
                <tr>
                  <th>Quote no.</th><th>Name</th><th>Date</th>
                  <th className="q-r">Cables</th><th className="q-r">Items</th><th className="q-r">Hours</th>
                  <th className="q-r">Discount</th><th className="q-r">Total excl. VAT</th><th className="q-r">Total incl. VAT</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ q, t }) => (
                  <tr key={q._id} className="q-clickable" onClick={() => open(q)} title="Open quote">
                    <td>{q.number}</td><td>{q.name}</td><td>{quoteDate(q.date)}</td>
                    <td className="q-r">{money(t.cables)}</td><td className="q-r">{money(t.items)}</td><td className="q-r">{money(t.hours)}</td>
                    <td className="q-r">{t.discount ? `-${money(t.discount)}` : ''}</td>
                    <td className="q-r">{money(t.net)}</td><td className="q-r">{money(t.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  <td className="q-r">{money(grand.cables)}</td><td className="q-r">{money(grand.items)}</td><td className="q-r">{money(grand.hours)}</td>
                  <td className="q-r">{grand.discount ? `-${money(grand.discount)}` : ''}</td>
                  <td className="q-r">{money(grand.net)}</td><td className="q-r">{money(grand.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuoteSummary;
